/**
 * The arithmetic behind `WeightPlanChart`, kept separate from the SVG for
 * the same reason `chartMath.ts` is split from `MeasurementChart.tsx`: it
 * can be read — and tested — as pure functions, without wading through
 * markup, and without the test runner having to load a CSS module import
 * transitively through the component file.
 */

export interface PlanBarGeometry {
  top: number;
  height: number;
  /** Rounded on the data end only, square where it meets the zero baseline —
   *  the same convention `MeasurementChart`'s own change strip uses, so the
   *  baseline reads as one continuous edge. */
  roundTop: boolean;
}

/**
 * Bar geometry for one week's actual change, or `null` when that week has no
 * bar at all — not enough readings to say, which must never be drawn as a
 * real zero.
 *
 * A week that genuinely did not move still gets a visible sliver straddling
 * the baseline (`minBarHeight`), because a bar of no height is otherwise
 * indistinguishable from a week with nothing to show — exactly the same
 * reasoning `MeasurementChart`'s change-strip stub is built on.
 */
export function planBarGeometry(
  change: number | null,
  y: number,
  zeroY: number,
  minBarHeight: number,
): PlanBarGeometry | null {
  if (change === null) return null;
  const roundTop = change >= 0;
  const trueHeight = Math.abs(y - zeroY);
  const height = Math.max(trueHeight, minBarHeight);
  const top = trueHeight < minBarHeight ? zeroY - minBarHeight / 2 : Math.min(y, zeroY);
  return { top, height, roundTop };
}

/**
 * The SVG path for one bar, rounded on the data end only (see
 * `PlanBarGeometry.roundTop`) — identical shape logic to `MeasurementChart`'s
 * own `changeBarPath`, kept local and pure so this component owns it rather
 * than reaching into another component's private helper.
 */
export function planBarPath(
  x: number,
  top: number,
  width: number,
  height: number,
  roundTop: boolean,
  radius: number,
): string {
  if (height <= 0 || width <= 0) return '';
  const r = Math.min(radius, width / 2, height);
  if (roundTop) {
    return `M ${x} ${top + height} L ${x} ${top + r} Q ${x} ${top} ${x + r} ${top} L ${x + width - r} ${top} Q ${x + width} ${top} ${x + width} ${top + r} L ${x + width} ${top + height} Z`;
  }
  return `M ${x} ${top} L ${x + width} ${top} L ${x + width} ${top + height - r} Q ${x + width} ${top + height} ${x + width - r} ${top + height} L ${x + r} ${top + height} Q ${x} ${top + height} ${x} ${top + height - r} L ${x} ${top} Z`;
}

export interface RatePoint {
  x: number;
  y: number | null;
}

/**
 * Splits the required-rate series into runs of consecutive, defined points —
 * so a week with no required rate breaks the line rather than being bridged
 * across, the same "a chart may simplify, but it may not invent" discipline
 * `linearPath` itself is built on. Each run is handed to `linearPath`
 * (needs at least two points to draw a segment; a lone point in a run of one
 * is drawn as a dot by the caller instead).
 */
export function requiredRatePolylines(points: readonly RatePoint[]): { x: number; y: number }[][] {
  const runs: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];
  for (const point of points) {
    if (point.y === null) {
      if (current.length > 0) runs.push(current);
      current = [];
    } else {
      current.push({ x: point.x, y: point.y });
    }
  }
  if (current.length > 0) runs.push(current);
  return runs;
}

/**
 * Widens a value domain so zero sits exactly in the middle.
 *
 * Without this the axis fits the data, so a run of good weeks pushes the
 * zero line up against the top of the plot and a bar has almost nowhere to
 * go downwards. That reads as if losing is the only direction the chart
 * knows about — and it is not: this chart is drawn for a gain goal too, and
 * a bad week on a loss goal is a bar below the line that has to have room to
 * be drawn at all.
 *
 * Keeping zero centred costs vertical resolution when every week is on the
 * same side, and buys a chart whose sign is legible at a glance from the
 * position of the baseline alone.
 */
export function symmetricAroundZero(domain: { min: number; max: number }): {
  min: number;
  max: number;
} {
  const reach = Math.max(Math.abs(domain.min), Math.abs(domain.max));
  // A domain with nothing in it stays as it was; forcing {-0, 0} would make
  // the scale degenerate rather than centred.
  return reach > 0 ? { min: -reach, max: reach } : domain;
}

/**
 * Turns "movement towards the target" into the weight change a scale would
 * actually have shown.
 *
 * The maths carries every weekly figure as movement TOWARDS the target,
 * which is what lets one set of rules serve a loss goal and a gain goal
 * alike. It is the wrong thing to print. Losing 1.2 kg came out as "1.2 kg",
 * and the owner read a bar rising above the zero line as having GAINED it:
 * "I actually want that to be -1.2 kg."
 *
 * So the chart shows the real delta — down is negative, up is positive,
 * whichever way the goal runs — and which side of the goal that falls on is
 * left to the required-pace line, which is flipped the same way and so sits
 * on the same side as the weeks that are keeping up with it.
 */
export function asWeightDelta(value: number, goingDown: boolean): number;
export function asWeightDelta(value: number | null, goingDown: boolean): number | null;
export function asWeightDelta(value: number | null, goingDown: boolean): number | null {
  if (value === null) return null;
  // Deliberately `-0` guarded: a flat week must read as "0.0", never "-0.0".
  const flipped = goingDown ? -value : value;
  return Object.is(flipped, -0) ? 0 : flipped;
}

/** The same flip for a band, which turns inside out when it is negated: the
 *  fastest healthy pace becomes the LOWEST number, not the highest. */
export function asWeightDeltaRange<T extends { min: number; max: number } | null | undefined>(
  range: T,
  goingDown: boolean,
): T extends null | undefined ? null : { min: number; max: number };
export function asWeightDeltaRange(
  range: { min: number; max: number } | null | undefined,
  goingDown: boolean,
): { min: number; max: number } | null {
  if (!range) return null;
  return goingDown ? { min: -range.max, max: -range.min } : { min: range.min, max: range.max };
}
